import { WIDGET_MAU_MAC_DINH } from '../../styles/tokens';

/**
 * Hợp đồng dữ liệu của module `tro-ly-ai` (ADR-0086), port từ `web-portal/src/modules/tro-ly-ai/types.ts` và hai
 * repository `context.repository.ts`, `assistant.repository.ts` của native.
 *
 * Giữ **nguyên tên trường của máy chủ** — camelCase như native (backend port 1:1). Riêng `DinhNghia` là
 * `snake_case` có chủ đích: đó là hợp đồng trong tài liệu lõi mà tầng hỏi–đáp đọc theo.
 *
 * Khác native: bỏ mọi thứ về tổ chức/phòng ban (`toChucId`, `X-Tenant-ID`, `organizationIds`) — tenant đã nằm
 * trong phiên, một database mỗi tenant (tương đương native chạy `TENANT_DON`).
 */

export type SourceKind = 'graphql' | 'dsn' | 'api';

/** Kết nối của nguồn `api` — địa chỉ gốc và cách xác thực. KHÔNG chứa khoá. */
export interface KetNoiApi {
  baseUrl: string;
  xacThuc: 'none' | 'bearer' | 'api_key' | 'basic';
  /** Tên header (hoặc tham số truy vấn) mang khoá khi `xacThuc = api_key`. */
  tenKhoa?: string;
  viTriKhoa?: 'header' | 'query';
  tenDangNhap?: string;
  thoiGianChoMs?: number;
}
export type SourceStatus = 'draft' | 'connected' | 'ready' | 'error';
/** `live` = câu hỏi đọc thẳng CSDL khách, không có bản sao. Chỉ nguồn DSN. */
export type AccessMode = 'copy' | 'live';
/** Số liệu của một câu trả lời đến từ đâu: đọc thẳng nguồn, hay từ bản sao. */
export type Freshness = 'live' | 'replica';

/**
 * Phạm vi phòng ban của bot/nguồn ở native. Platform không có trục phòng ban (một tenant = một tổ chức), nên
 * giao diện **không** vẽ ô chọn này; trường vẫn đi khứ hồi nguyên giá trị để thân `PATCH` không mất nó.
 */
export type PhamViBot = 'phong_ban' | 'phong_ban_con' | 'toan_to_chuc';

export interface GraphqlSource {
  id: string;
  name: string;
  kind?: SourceKind;
  /** Với nguồn `dsn`: chuỗi ĐÃ CHE (`user:***@host/db`). Không bao giờ chứa mật khẩu. */
  endpoint: string;
  luocDo?: string | null;
  /** Mô tả nghiệp vụ — tiêu đề vùng của nguồn trong câu trả lời nhiều nguồn. KHÔNG đi vào lời nhắc. */
  moTa?: string;
  /** Định hướng bằng lời cho CẢ NGUỒN, đi thẳng vào gói ngữ cảnh của MỌI lượt hỏi. */
  ghiChuNghiepVu?: string;
  dialect: string;
  /** Nguồn tạo trước khi có chế độ này không mang khoá — coi là `copy`. */
  accessMode?: AccessMode;
  metadataUrl: string | null;
  status: SourceStatus;
  lastError: string | null;
  autoSync: boolean;
  syncIntervalSeconds: number;
  db: {
    host: string | null;
    port: number | null;
    name: string | null;
    user: string | null;
    schema: string | null;
    readerUser: string | null;
  };
  hasSecrets: boolean;
  phamVi?: PhamViBot;
  createdAt: string | null;
  /** Chỉ có ở phản hồi TẠO nguồn, đúng một lần. */
  notifySecret?: string;
  /** Chỉ có ở nguồn `api`. */
  api?: KetNoiApi;
  /** Trả lời theo bản khai (`cau_hoi`) hay theo khuôn. Nguồn API luôn `khuon`. */
  cheDo?: CheDoKhai;
}

export interface SyncRun {
  id: string;
  sourceId: string;
  table: string;
  status: 'ok' | 'stopped' | 'skipped';
  mode: string;
  pages: number;
  rowsRead: number;
  rowsWritten: number;
  rowsDeleted: number;
  elapsedMs: number;
  message: string | null;
  startedAt: string;
}

export interface QueryRun {
  id: string;
  sourceId: string | null;
  actor: string | null;
  question: string | null;
  sql: string | null;
  status: 'ok' | 'rejected' | 'failed';
  refusals: Refusal[] | null;
  tables: string[] | null;
  rowCount: number | null;
  elapsedMs: number | null;
  createdAt: string;
}

/** Một lời từ chối của bộ canh cổng SQL (`details.refusals` của Problem Details). */
export interface Refusal {
  code: string;
  message: string;
  /** Bảng và cột lời từ chối nói tới, khi máy chủ xác định được — để dựng đường tới đúng ô khai báo còn trống. */
  bang?: string;
  cot?: string;
}

export interface SqlContext {
  dialect: string;
  schema: string;
  maxRows: number;
  syncedAt: string | null;
  rules: string[];
  tables: { name: string; label?: string; rowCount?: number; columns: unknown[] }[];
  glossary: GlossaryTerm[];
  samples: { question: string; sql: string }[];
  /** CHỈ có `id` — tên bộ lấy từ module tri thức. */
  knowledgeCollections?: { id: string }[];
  promptText: string;
}

export interface GlossaryTerm {
  term: string;
  meaning: string;
}

export interface QueryResult {
  sql: string;
  dialect: string;
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number;
  truncated: boolean;
  elapsedMs: number;
  tables: string[];
  syncedAt: string | null;
  freshness?: Freshness;
  warnings: string[];
}

export interface PermTable {
  name: string;
  title: string;
  enabled: boolean;
  columns: { name: string; label: string }[];
}

export type PermLevel = 'full' | 'tables' | 'none';

export interface PermGrant {
  role: string;
  level: PermLevel;
  tables: { table: string; deniedColumns: string[] }[];
}

// ── Khuôn nghiệp vụ ───────────────────────────────────────────────────────────

/** `cau_hoi` = khai bằng câu hỏi. `khuon` = khai bằng khuôn. Không bao giờ cả hai. */
export type CheDoKhai = 'cau_hoi' | 'khuon';

export interface NhomKhuon {
  id: string;
  ten: string;
  moTa: string;
  thuTu: number;
  soKhuon: number;
}

export interface PhamViKhuon {
  bang: string[];
  cot: Record<string, string[]>;
  /** Cột mà khuôn CỘNG — lời khai mạnh nhất một khuôn đưa ra. */
  congDon: Record<string, string[]>;
}

/** `cau_lenh`: mô hình VẪN viết SQL, khuôn thu hẹp chỗ nó được viết. `thu_tuc`: mô hình chọn nghiệp vụ, điền tham số. */
export type KieuKhuon = 'cau_lenh' | 'thu_tuc' | 'api';

/** Một tham số thủ tục — DANH SÁCH TRẮNG thứ mô hình được phép điền. */
export interface ThamSoKhuon {
  ten: string;
  kieu: 'int' | 'string' | 'date' | 'decimal' | 'bool';
  moTa: string;
  batBuoc: boolean;
  /** `dang_nhap` = nền tảng tự điền từ danh tính người hỏi, mô hình không chạm. */
  nguon: '' | 'dang_nhap';
  /** Giá trị CỐ ĐỊNH của khuôn. Luôn gửi; trợ lý không điền và không đổi được. */
  giaTri: string;
  /** Chỉ ở khuôn `api`: tham số đi vào đường dẫn, chuỗi truy vấn hay thân JSON. */
  viTri?: 'query' | 'path' | 'body';
}

export interface CotHienThi {
  field: string;
  nhan: string;
  kieu: 'text' | 'tien' | 'ngay' | 'so' | 'phan_tram';
}

export interface Khuon {
  id: string;
  nhomId: string;
  ten: string;
  moTa: string;
  cachGoi: string[];
  cauHoiMau: string[];
  kieu: KieuKhuon;
  sql: string;
  tenThuTuc: string;
  /** Chỉ ở khuôn `api`: đường dẫn nối sau địa chỉ gốc của nguồn, có `{tham_so}`. */
  duongDan?: string;
  phuongThuc?: 'GET' | 'POST';
  thamSo: ThamSoKhuon[];
  cot: CotHienThi[];
  tongHop: Record<string, string>;
  tomTat: string;
  tieuDe: string;
  dialect: 'postgres' | 'mssql';
  phamVi: PhamViKhuon;
  daChayThu: boolean;
  chayThuLuc: string | null;
  verified: boolean;
  unverifiedReason: string;
  /** Đã vào được bộ chọn chưa — nền tảng tính từ ba cờ trên. */
  vaoBoChon: boolean;
  createdBy: string;
}

/** Thân tạo/sửa một khuôn. */
export interface KhuonInput {
  id?: string;
  nhomId: string;
  ten: string;
  moTa?: string;
  cachGoi?: string[];
  cauHoiMau?: string[];
  kieu?: KieuKhuon;
  sql?: string;
  tenThuTuc?: string;
  duongDan?: string;
  phuongThuc?: 'GET' | 'POST';
  thamSo?: ThamSoKhuon[];
  cot?: CotHienThi[];
  tongHop?: Record<string, string>;
  tomTat?: string;
  tieuDe?: string;
  dialect?: string | null;
}

export interface DanhSachKhuon {
  sourceId: string;
  cheDo: CheDoKhai;
  dialect: string;
  nhom: NhomKhuon[];
  khuon: Khuon[];
}

/** Khuôn mà CHÍNH người đang đăng nhập dùng được — không kèm câu lệnh. */
export interface KhuonCuaToi {
  sourceId: string;
  role: string;
  khuon: { id: string; ten: string; moTa: string; nhomId: string; nhom: string; cauHoiMau: string[] }[];
}

/** `chon` = chỉ nhóm/khuôn có tên trong danh sách. */
export type KhuonLevel = 'full' | 'chon' | 'none';

export interface KhuonGrant {
  role: string;
  level: KhuonLevel;
  nhom: string[];
  khuon: string[];
  /** Thắng cả `nhom` lẫn `full`: cấp cả nhóm rồi chặn riêng một khuôn thì chặn thắng. */
  chan: string[];
}

export interface KhuonMaTran {
  sourceId: string;
  cheDo: CheDoKhai;
  levels: KhuonLevel[];
  nhom: { id: string; ten: string; khuon: { id: string; ten: string; vaoBoChon: boolean }[] }[];
  grants: KhuonGrant[];
}

export type BotLevel = 'full' | 'chon' | 'none';

/** Vai nào THẤY được bot nào. */
export interface BotGrant {
  role: string;
  level: BotLevel;
  chatbot: string[];
  /** Thắng cả `chatbot` lẫn `full`. */
  chan: string[];
}

export interface BotMaTran {
  levels: BotLevel[];
  chatbot: { id: string; ten: string; bat: boolean }[];
  grants: BotGrant[];
  /** Tên hiển thị của từng vai (có thể vắng — khi ấy dùng mã). */
  vai?: { role: string; ten: string }[];
}

/** Những gì sẽ đổi nếu gạt công tắc chế độ khai — phải hiện TRƯỚC khi gạt. */
export interface DoiChieuCheDo {
  sourceId: string;
  cheDo: CheDoKhai;
  cheDoCoThe: CheDoKhai[];
  soKhuon: number;
  soKhuonSanSang: number;
  vaiKhongDungDuocKhuonNao: string[];
  khuonChuaChayThu: { id: string; ten: string }[];
  khuonBiHa: { id: string; ten: string; lyDo: string }[];
}

export interface KetQuaChayThu {
  khuon: Khuon;
  /** Với khuôn thủ tục: `EXEC USP_x @nam=2026` — để ĐỌC, không phải để chạy. */
  sql: string;
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number;
  elapsedMs: number;
  /** Dòng tổng hợp — tập kết quả thứ hai của thủ tục. */
  tongHop?: Record<string, unknown> | null;
  soTap?: number;
  tomTat?: string;
}

export interface AppNotification {
  id: string;
  sourceId: string;
  kind: 'new_data' | 'schema_change' | 'sync_failed';
  message: string;
  createdAt: string;
  readAt: string | null;
  syncedAt: string | null;
}

export interface ReplicaTableState {
  table: string;
  rowCount: number;
  syncedAt: string | null;
  watermark: string | null;
  lastStatus: string;
  lastMessage: string;
  incremental: boolean;
  deleteStrategy: string;
}

/** Tiến độ chu kỳ đồng bộ chạy nền. */
export interface TienDoDongBo {
  /** Chỉ `true` khi nhịp tim còn mới; máy chủ đã tính sẵn. */
  dangChay: boolean;
  /** Cờ còn bật nhưng nhịp tim đã tắt: tiến trình chết giữa chừng. */
  biNgat: boolean;
  batDau: string;
  capNhat: string;
  ketThuc: string | null;
  soBang: number;
  bangXong: number;
  bang: string | null;
  thuTu: number;
  daDoc: number;
  dangDoHoSo?: boolean;
}

export interface TrangThaiBanSao {
  sourceId: string;
  dialect: string;
  accessMode?: AccessMode;
  tables: ReplicaTableState[];
  dongBo: TienDoDongBo | null;
}

export interface SyncOutcome {
  table: string;
  status: 'ok' | 'stopped' | 'skipped';
  message: string;
  stats: {
    mode: string;
    pages: number;
    rowsRead: number;
    rowsWritten: number;
    rowsDeleted: number;
    elapsedMs: number;
  };
  issues: { kind: string; column: string | null; detail: string; rows: number }[];
}

export interface Finding {
  kind: 'enum' | 'link' | 'dead_table';
  table: string;
  column: string | null;
  detail: string;
  patch: Record<string, unknown>;
  confidence: number;
}

export interface KetQuaDoTim {
  findings: Finding[];
  /** Hết trần giờ trước khi dò xong — `findings` chỉ là phần đã dò kịp. */
  hetGio: boolean;
  boQua: number;
}

// ── Nguồn: khai, kiểm, dò ─────────────────────────────────────────────────────

/**
 * Thân khai một nguồn. Máy chủ đặt `extra="forbid"`: thừa một khoá là 422.
 *
 * KHÔNG có `kind`: máy chủ tự suy loại nguồn từ chỗ có `dsn`, `endpoint` hay `api`.
 */
export interface CreateSourcePayload {
  /** Định danh SQL — đi vào TÊN cơ sở dữ liệu của bản sao, KHÔNG đổi được sau khi dựng kho. `^[a-z][a-z0-9_]*$`, tối đa 48. */
  id: string;
  name: string;
  /** Endpoint GraphQL. Loại trừ với `dsn` và `api`. */
  endpoint?: string;
  /** Chuỗi kết nối khách hàng dán vào. Mật khẩu tách sang kho bí mật ngay khi tạo. */
  dsn?: string | null;
  luocDo?: string;
  dialect?: string;
  metadataUrl?: string | null;
  /** Khoá gọi GraphQL. Vào kho bí mật, không bao giờ trả ra. */
  graphqlToken?: string | null;
  /** Để trống thì nền tảng tự sinh và trả về ĐÚNG MỘT LẦN, lúc tạo. */
  notifySecret?: string | null;
  autoSync?: boolean;
  /** 60..86400 giây. */
  syncIntervalSeconds?: number;
  /** Bỏ trống thì máy chủ chọn: DSN đọc trực tiếp, GraphQL sao chép. */
  accessMode?: AccessMode;
  api?: KetNoiApi;
  /** Khoá của nguồn API. Vào kho bí mật, không bao giờ trả ra. */
  apiSecret?: string | null;
}

/** Chẩn đoán kết nối — CHỈ có với nguồn DSN: kiểm cả QUYỀN chứ không chỉ kết nối. */
export interface ChanDoanDsn {
  phuongNgu: string;
  phienBan: string;
  luocDo: string;
  soBang: number;
  /** Thủ tục/hàm tài khoản GỌI ĐƯỢC trong lược đồ. */
  soThuTuc?: number;
  /** Không có bảng nào đọc được nhưng có thủ tục: chỉ hỏi qua khuôn thủ tục. */
  chiThuTuc?: boolean;
  cheDo?: string;
  luocDoKhac: string[];
  /** Phiên có ở chế độ chỉ-đọc không — SQL Server không có công tắc này, quyền tài khoản là lớp bảo vệ duy nhất. */
  chiDoc: boolean;
  ghiChuChiDoc: string;
}

export interface TestSourceResult {
  ok: boolean;
  chanDoan?: ChanDoanDsn;
  chiThuTuc?: boolean;
  cheDo?: string;
  tables: { sourceType: string; queryField: string; columns: number; supportsIncremental: boolean }[];
}

/** Một bảng máy ĐỌC ĐƯỢC ở nguồn, kèm bản khai ĐỀ XUẤT — chưa lưu gì cả. */
export interface IntrospectBang {
  sourceType: string;
  /** Số dòng ƯỚC LƯỢNG. `null`/vắng = chưa biết; `0` = đã đo và rỗng — chỉ `0` mới đủ căn cứ để giấu bảng. */
  soDong?: number | null;
  queryField: string;
  supportsIncremental: boolean;
  fingerprint: string;
  /** Gửi nguyên khối lên `saveTables()`, không nhặt từng field. */
  suggestion: Record<string, unknown> & {
    name: string;
    title?: string;
    primaryKey?: string[];
    columns?: { name: string; label?: string; type?: string }[];
    /** Cột mốc thời gian. Bỏ nó khỏi `columns` là bảng tụt xuống chép đầy mỗi chu kỳ — giao diện phải khoá nó. */
    updatedAtField?: string | null;
    links?: { column: string; toTable?: string; toColumn?: string }[];
  };
  /** Những chỗ máy ĐÃ ĐOÁN — mỗi dòng là một câu hỏi cho người. */
  notes: string[];
  links: unknown[];
}

export type LyDoBoQua = 'qua_nhieu_cot' | 'khoa_chinh_qua_dai' | 'khoa_chinh_nullable' | 'khac';

export interface BangBoQua {
  bang: string;
  lyDo: LyDoBoQua;
  chiTiet: string;
}

export interface IntrospectResult {
  sourceId: string;
  tables: IntrospectBang[];
  canhBao: string[];
  /** Cùng danh sách ấy nhưng có cấu trúc. Vắng ⇒ giao diện rơi về `canhBao`. */
  boQua?: BangBoQua[];
}

/** Hồ sơ ĐÃ LƯU, kèm `promptText` — đúng khối chữ sẽ đi vào lời nhắc. */
export interface ProfileResult {
  sourceId: string;
  profiledAt: string | null;
  tables: Record<string, unknown>[];
  promptText: string;
}

/** Kết quả CHẠY dò hồ sơ — đếm, không phải nội dung. */
export interface RunProfileResult {
  sourceId: string;
  tables: number;
  columns: number;
  rows: number;
  ms?: number;
  boQuaTat?: number;
  bangs?: { bang: string; soDong: number; trenMau: boolean; soCot: number; cotKhongDo: string[] }[];
  bangLoi?: { bang: string; loi: string }[];
}

export interface QuestionCandidate {
  question: string;
  table?: string;
  kind?: string;
  columns?: string[];
  /** Đã có trong danh sách chốt. */
  alreadySaved: boolean;
}

export interface QuestionItem {
  question: string;
  table?: string;
  kind?: string;
  note?: string;
}

/**
 * Một định nghĩa nghiệp vụ: thuật ngữ → BIỂU THỨC SQL. `snake_case` CỐ Ý — hợp đồng tài liệu lõi.
 */
export interface DinhNghia {
  term: string;
  sql_expr: string;
  /** `bang` hoặc `bang.cot` — NGƯỜI KHAI điền, để phép dò lệch khi lược đồ đổi khớp được. */
  depends_on: string[];
  /** Ba trường dưới do NỀN TẢNG đặt, không gửi lên. */
  defined_by?: string;
  defined_at?: string | null;
  verified?: boolean;
  unverified_reason?: string;
}

export interface SampleItem {
  id: string;
  sourceId: string;
  question: string;
  sql: string;
  generatedSql: string | null;
  note: string | null;
  createdAt: string;
}

export interface XoaBangKetQua {
  bang: string;
  xoaKho: boolean;
  dinhNghiaLech: string[];
  quyenDaGo: number;
}

// ── Phân quyền dữ liệu ────────────────────────────────────────────────────────

export interface PermAccounts {
  role: string;
  /** Người đang đăng nhập có sửa được ma trận không — nền tảng TÍNH, đừng tự suy. */
  canManage: boolean;
  accounts: { role: string; label: string; canManage: boolean }[];
  levels: string[];
}

export interface QuyenHieuLuc {
  role: string;
  sources: (PermGrant & { sourceId: string })[];
}

export interface MaTranQuyen {
  sourceId: string;
  tables: PermTable[];
  grants: PermGrant[];
}

// ── Hỏi–đáp ───────────────────────────────────────────────────────────────────

/** Một chặng trong bảng đo latency. `llm` là chỉ số quyết định hơn mọi thứ khác. */
export interface NodeTiming {
  node: string;
  ms: number;
  llm: boolean;
  tokensIn?: number;
  tokensOut?: number;
  /** Token trúng đệm — nói thẳng lời gọi này NÓNG hay NGUỘI. */
  cached?: number;
  /** Chạy CHỒNG LẤN với các chặng cùng cờ — cộng `ms` của chúng là đếm một khoảng thời gian nhiều lần. */
  songSong?: boolean;
  /** Riêng chặng đọc thành tiếng: bao lâu thì MẨU ĐẦU ra tới tai (ms). */
  msMauDau?: number;
}

export type AskKind =
  | 'data'
  | 'analysis'
  | 'knowledge'
  | 'clarify'
  | 'capability'
  | 'freshness'
  | 'greeting'
  | 'kich_ban'
  | 'unsupported';

/** Dạng trực quan do máy chủ chọn (thuần quy tắc). */
export interface AskDisplay {
  dang: string;
  x?: string | null;
  y?: string | null;
  loat?: string | null;
  kich_thuoc?: string | null;
  chi_tieu?: string[];
  /** Vì sao dạng này — kể cả khi nó KHÁC dạng người dùng xin. */
  vi_sao?: string;
  theoYeuCau?: boolean;
  /** Dạng VẼ ĐƯỢC với dữ liệu này. Giao diện không dựng bản thứ hai của luật ấy. */
  hopLe?: string[];
  /** Trục cho từng dạng hợp lệ, để đổi dạng không phải gọi lại máy chủ. */
  truc?: Record<string, { x?: string | null; y?: string | null; loat?: string | null }>;
}

/** Trạng thái một quy trình nhiều bước của chatbot. `null` ở MỌI lượt hỏi thường. */
export interface KichBanTienDo {
  ten: string;
  buoc: number;
  tongBuoc: number;
  /** Trường còn thiếu. Rỗng dần qua từng lượt; hết là đủ điều kiện đóng. */
  conThieu: string[];
  xong: boolean;
  /** Lý do giá trị vừa nhập bị từ chối (dùng NHÃN người vận hành đặt), hoặc `null`. */
  loi?: string | null;
  /** ⚠️ PHẢI GỬI LẠI Ở LƯỢT SAU trong `history[].kichBan` — trợ lý không có phiên. */
  trangThai: {
    kichBan: string;
    daThu: Record<string, string>;
    buoc: number;
  };
}

export interface Passage {
  fileName: string;
  collectionName: string;
  text: string;
  score: number;
  chunkIndex: number;
  /** `"web"` = đoạn lấy từ trang web của tổ chức, không phải tài liệu đã duyệt. */
  nguon?: string;
  /** Địa chỉ trang, chỉ có với đoạn web. */
  url?: string;
}

/** Kết quả của MỘT nguồn trong một lượt hỏi nhiều nguồn. */
export interface KetQuaNguon {
  sourceId: string;
  ten: string;
  /** `n1`, `n2`… — khớp nhãn `{{nguon: nX}}` mà máy chủ vẽ vào lời văn. */
  biDanh: string;
  sql: string;
  columns: string[];
  rows: Record<string, unknown>[];
  nhanCot?: Record<string, string>;
  rowCount: number;
  truncated: boolean;
  syncedAt?: string | null;
  freshness?: Freshness | null;
  warnings: string[];
  attempts: number;
  /** Vì sao nguồn này không có số liệu. Rỗng = có. */
  loi: string;
}

export interface AskStep {
  step: string;
  detail: string;
}

export interface AskResult {
  question: string;
  /** Mã tra cứu của lượt này. */
  traceId?: string;
  /** Từng nguồn của lượt này. Vắng ở lượt cũ đọc lại từ lịch sử — mọi chỗ đọc phải chịu `undefined`. */
  nguon?: KetQuaNguon[];
  /** Nguồn đã gắn mà lượt này KHÔNG hỏi, kèm lý do — phải vẽ ra, không được bỏ qua. */
  nguonBoQua?: { sourceId: string; ten: string; vi_sao: string }[];
  /** Câu hỏi có ĐÒI liệt kê không. KHÔNG còn là cờ vẽ bảng — `veBang` là. */
  hoiDanhSach?: boolean;
  /** Lượt này có CẦN bảng không — máy chủ tính. Vắng thì lùi về `hoiDanhSach`. */
  veBang?: boolean;
  nhanCot?: Record<string, string>;
  nodes?: NodeTiming[];
  soLanGoiLlm?: number;
  /** Mô hình đã dùng THẬT cho lượt này (`provider · model`). */
  model?: string;
  /** Tên hiện cho người dùng cuối — `model` lộ nhà cung cấp. */
  tenCongKhai?: string;
  answer: string;
  /** Loại câu hỏi trợ lý đã NHẬN DẠNG — một câu hỏi lại không phải một câu trả lời. */
  kind?: AskKind;
  display?: AskDisplay;
  kichBan?: KichBanTienDo | null;
  /** Tầng 1: số KHÔNG quy về được ô nào → đã bị chặn. RỖNG ≠ "đã được kiểm". */
  unsourcedNumbers: string[];
  /** Tầng 2: số TÍNH RA từ hai ô (chênh lệch, tổng). */
  derivedNumbers?: string[];
  /** ⚠️ ĐỪNG VẼ: đã nằm sẵn trong `answer` dưới dạng "💬 Nhận định — …". */
  labelled?: { kind: string; text: string }[];
  anomalies?: { loai: string; message: string; column?: string; label?: string }[];
  /** `rieng` = câu viết cho người vận hành, không vẽ xuống dưới câu trả lời. */
  checks?: { loai: string; message: string; rieng?: boolean }[];
  /** Lỗi HÌNH DẠNG lời văn — dành cho người VẬN HÀNH. */
  proseIssues?: string[];
  /** Câu đúng với phần bảng trong lời nhắc nhưng SAI với cả bảng — vẽ cạnh `unsourcedNumbers`. */
  partialViewClaims?: string[];
  /** Chạm trần tổng thời gian nên phần lời văn bị bỏ. Phải vẽ ra. */
  quaTran?: boolean;
  /** SUY LUẬN của mô hình — giao diện phải đánh dấu là đề xuất. */
  suggestions?: string[];
  followUps?: string[];
  sql: string;
  explanation: string;
  assumptions: string[];
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number;
  truncated: boolean;
  syncedAt: string | null;
  freshness?: Freshness | null;
  warnings: string[];
  /** Đoạn tài liệu đã đưa cho mô hình — để kiểm được bằng cách nhìn. */
  passages?: Passage[];
  steps: AskStep[];
  attempts: number;
  elapsedMs: number;
}

/** Cấp độ văn xuôi. */
export type MucVan = 'gon' | 'phan_tich' | 'khuyen_nghi';
/** Không gửi = `tu_dong`: máy chủ đoán từ chữ trong câu hỏi. */
export type CongTacBieuDo = 'tu_dong' | 'co' | 'khong';
/** Không gửi = `tu_dong`: chỉ tra web khi hai nguồn kia rỗng. */
export type CongTacWeb = 'tu_dong' | 'co' | 'khong';

/** Một lượt đã xong, đúng hình dạng trợ lý đọc ở `history`. */
export interface LuotLichSu {
  question: string;
  answer: string;
  kichBan?: KichBanTienDo | null;
  /** Lượt trợ lý xin thêm thông tin — câu kế tiếp là câu TRẢ LỜI. */
  hoiLai?: boolean;
  /** Câu lệnh lượt ấy đã chạy — lời văn không nói ra bộ lọc, thiếu nó thì "chia theo tháng" mất lọc. */
  sql?: string;
}

/** Thân `POST /ask` và `POST /ask/stream`. */
export interface AskBody {
  question: string;
  /** Một trong ba phải có: `chatbotId` thì bản khai của bot quyết định tra ở đâu. */
  sourceId?: string;
  /** Nhiều nguồn cho MỘT lượt — mỗi nguồn một câu SQL riêng, không JOIN chéo. */
  sourceIds?: string[];
  chatbotId?: string;
  /** Cuộc trò chuyện đang mở — chỉ để Nhật ký dẫn sang đúng chỗ. */
  conversationId?: string;
  sql?: string;
  maxRows?: number;
  /** Provider cho RIÊNG lượt này. */
  model?: string;
  mucVan?: string;
  doDai?: string;
  /** Mã giọng máy chủ. Không gửi = không tổng hợp tiếng. */
  giong?: string;
  /** Bên gọi ĐỌC ĐƯỢC âm thanh theo dòng (`pcm` thô). */
  tiengDan?: boolean;
  bieuDo?: string;
  web?: string;
  history?: LuotLichSu[];
}

/** Một mục trong ô chọn mô hình. `id` là thứ gửi ngược lên; vắng ở máy chủ cũ — khi ấy dùng `provider`. */
export interface MucModel {
  id?: string;
  provider: string;
  model: string;
  available: boolean;
  note: string;
  nhan?: string;
  tenCongKhai?: string;
  macDinh?: boolean;
}

export interface AssistantHealth {
  ok: boolean;
  model: string;
  llmConfigured: boolean;
  /** Mọi lựa chọn, kèm cả cái CHƯA cấu hình. */
  models?: MucModel[];
  /** Giọng MÁY CHỦ đọc được. Vắng/rỗng ⇒ ẩn hẳn nút loa. */
  giong?: { ma: string; nhan: string; ngon_ngu: string }[];
}

/** Đề xuất mô hình đọc ra từ tài liệu mô tả nghiệp vụ. */
export interface DeXuatKhai {
  ma: string;
  giaTri: string;
  viSao: string;
  /** Chỉ câu hỏi nghĩa mã mới có: giá trị → nghĩa. */
  cap?: Record<string, string>;
}

/** Vì sao một câu KHÔNG có đề xuất — năm nhánh, năm việc khác nhau. */
export type TrangThaiCauKhai =
  | 'dung_duoc'
  | 'khong_thay_trong_tai_lieu'
  | 'lo_hong'
  | 'json_hong'
  | 'rong'
  | 'sai_lua_chon'
  | 'trung';

export interface NhatKyKhai {
  moTaKyTu: number;
  doan: { tong: number };
  lo: { tong: number; hong: number; jsonHong: number };
  /** Mã mô hình bịa ra — không có trong danh sách đã gửi. */
  maLa: string[];
  cau: { ma: string; trangThai: TrangThaiCauKhai; ghiChu: string }[];
}

export interface CauHoiDeXuat {
  ma: string;
  noiDung: string;
  viSao?: string;
  nhieuLuaChon?: boolean;
  luaChon?: { giaTri: string; nhan?: string }[];
}

/** Ba núm đệm lượt hỏi. */
export interface CauHinhDem {
  bat: boolean;
  hanGiay: number;
  sucChua: number;
  /** Đệm cả lượt đọc THẲNG hệ thống của khách. */
  demTrucTiep?: boolean;
  /** Hạn đệm CÂU LỆNH, giây. `0` = tắt. */
  sqlHanGiay?: number;
  /** Hạn riêng cho lượt CÓ GIỌNG ĐỌC, giây. Máy chủ luôn lấy `min()` với `hanGiay`. */
  tiengHanGiay?: number;
}

export interface DemThongKe {
  bat: boolean;
  soMuc: number;
  trung: number;
  truot: number;
  tiLeTrung: number;
  /** `env` ⇒ chưa có Redis, chỉnh trên giao diện sẽ KHÔNG có tác dụng. */
  nguonCauHinh?: string;
  /** `redis` = kho dùng chung; `tien_trinh` = mỗi worker một kho. */
  cheDo?: string;
  /** Cảnh báo do MÁY CHỦ soạn — đọc từ đây, không viết cứng ở giao diện. */
  canhBao?: string;
  demTieng?: boolean;
}

export interface CauHinhDemRes {
  cauHinh: CauHinhDem;
  /** Chặn dưới/trên do MÁY CHỦ công bố. */
  gioiHan: {
    hanGiay: [number, number];
    sucChua: [number, number];
    sqlHanGiay?: [number, number];
    tiengHanGiay?: [number, number];
  };
  nguon: string;
  thongKe?: DemThongKe;
}

/** Sự kiện của luồng `POST /ask/stream` — bảy loại, loại nằm trong `type` của gói `data`. */
export type LoaiSuKienHoi = 'step' | 'van' | 'van_thay' | 'tieng' | 'done' | 'suggestions' | 'error';
export interface SuKienHoi {
  type: LoaiSuKienHoi;
  [khoa: string]: unknown;
}

// ── Phỏng vấn khai báo ────────────────────────────────────────────────────────

export interface GoiY {
  giaTri: string;
  /** `tu_dien` (suy từ TÊN) · `ho_so` (suy từ GIÁ TRỊ đã dò) · `tep_tra_loi`. */
  nguon: string;
  viSao: string;
  /** `cao` · `vua` · `thap`. Suy từ TÊN không bao giờ là `cao`. */
  doTin: string;
  cap?: Record<string, string>;
}

export interface CauHoiKhai {
  ma: string;
  bang: string;
  noiDung: string;
  viSao: string;
  /** `spec` (bản khai bảng) hoặc `dinh_nghia`. */
  dich: string;
  uuTien: number;
  /** `sai_am_tham` · `bi_tu_choi` · `kho_doc` — hỏng KIỂU GÌ nếu không trả lời. */
  hauQua: string;
  nhieuLuaChon: boolean;
  traLoiTuDo: boolean;
  luaChon: { giaTri: string; nhan: string; bangChung: string }[];
  /** Câu trả lời ĐỀ NGHỊ — để soát, KHÔNG phải lời khai. */
  goiY: GoiY | null;
}

export interface TraLoiPhongVan {
  ma: string;
  giaTri?: string;
  nhieu?: string[];
  /** Giá trị → nghĩa, cho câu hỏi nghĩa mã. */
  cap?: Record<string, string>;
}

export interface PhongVanResult {
  /** Câu của ĐÚNG MỘT bảng — `bang` nói là bảng nào. */
  cauHoi: CauHoiKhai[];
  bang: string | null;
  /** Chỉ mục MỌI bảng còn câu, đã xếp theo mức thiệt hại. */
  theoBang: { ten: string; so: number }[];
  /** Số bảng chưa ai trả lời câu "bảng này có đáng dùng không". */
  chuaXacNhan: number;
  /** Số câu chưa khai. `0` = bản khai đã đủ. */
  conThieu: number;
  theoHauQua: Record<string, number>;
  nhanBang: Record<string, string>;
  coGoiY: number;
}

export interface KetQuaGhiPhongVan {
  daGhi: string[];
  boQua: string[];
  deLen?: string[];
}

// ── Hội thoại ─────────────────────────────────────────────────────────────────

export interface HoiThoaiMessage {
  id: string;
  author: string;
  text: string;
  turn?: number;
  payload?: Record<string, unknown>;
}

export interface HoiThoai {
  id: string;
  title: string;
  /** Nguồn của đoạn — màn Hỏi đáp khôi phục lựa chọn nguồn từ đây. Rỗng với đoạn của chatbot. */
  scopeIds?: string[];
  createdAt?: string;
  updatedAt?: string;
  messages?: HoiThoaiMessage[];
}

/** Một đoạn trò chuyện trên màn Nhật ký — mọi vai, cả Trợ lý AI lẫn chatbot. */
export interface HoiThoaiNhatKy {
  id: string;
  title: string;
  loai: 'tro_ly' | 'chatbot';
  nhan: string;
  /** Vai đã tạo đoạn. Khách trên khung nhúng mang dạng `nhung:<mã phiên>`. */
  vai: string;
  luot: number;
  createdAt: string;
  updatedAt: string;
}

export interface LocNhatKyHoiThoai {
  ngay?: number;
  loai?: '' | 'tro_ly' | 'chatbot';
  q?: string;
  limit?: number;
}

export interface SuDung {
  ngay: number;
  luotHoi: {
    tong: number;
    ok: number;
    biTuChoi: number;
    hong: number;
    p50Ms: number | null;
    p95Ms: number | null;
  };
  /** `ten` rơi về `nguon` (mã) khi nguồn đã bị xoá. */
  theoNguon: { nguon: string; ten: string; n: number; khongOk: number }[];
  theoVai: { vai: string; n: number }[];
  theoNgay: { ngay: string; n: number }[];
  /** 24 khung giờ (0–23, giờ Việt Nam). Vắng thì ẩn công tắc "theo giờ". */
  theoGio?: { gio: number; n: number }[];
  hoiThoai: {
    id: string;
    loai: 'tro_ly' | 'chatbot';
    nhan: string;
    doan: number;
    luot: number;
    ganNhat: string | null;
  }[];
  /** Câu hỏi hay gặp nhất, đã GOM các biến thể. */
  cauThuongGap: { cau: string; n: number }[];
  /** Những thứ số trên KHÔNG nói được — vẽ ra, đừng nuốt. */
  khongDoDuoc: string[];
}

// ── Chatbot ───────────────────────────────────────────────────────────────────

export type ChatbotMode = 'knowledge_only' | 'open';

/**
 * Kênh giao tiếp — MỘT công tắc chi phối cả hai chiều tiếng. Chữ LUÔN hiện ở cả ba.
 *   van_ban    không mic, không phát tiếng
 *   giong_noi  có mic, TỰ phát tiếng câu trả lời
 *   ca_hai     có mic, có nút loa nhưng KHÔNG tự phát
 */
export type CheDoGiaoTiep = 'van_ban' | 'giong_noi' | 'ca_hai';

export const CHE_DO_GIAO_TIEP: CheDoGiaoTiep[] = ['van_ban', 'giong_noi', 'ca_hai'];

/** Số nguồn tối đa gắn được vào một bot — khớp `TRAN_NGUON` của backend (vượt là 422). Trần CHI PHÍ. */
export const TRAN_NGUON = 15;

export interface NguonGon {
  id: string;
  name: string;
}
export type WidgetPosition = 'bottom-right' | 'bottom-left';

export interface Faq {
  question: string;
  answer: string;
}

/** Kiểu của một trường thu thập. KHÔNG có kiểu "tên người": không luật nào đúng cho mọi cái tên. */
export type KieuTruong = 'van_ban' | 'so' | 'dien_thoai' | 'email' | 'ngay';

export interface TruongBatBuoc {
  ten: string;
  kieu: KieuTruong;
}

export interface BuocHoiThoai {
  ten: string;
  /** Bật thì lượt ấy phải ghi lại giá trị người dùng nói. */
  thuThap: boolean;
  kieu: KieuTruong;
}

export interface Scenario {
  name: string;
  trigger: string;
  reply: string;
  /** API LUÔN trả object; vẫn nhận `string` vì trạng thái biểu mẫu cục bộ có thể còn dạng cũ. */
  requiredFields: (string | TruongBatBuoc)[];
  /** Các bước, THEO THỨ TỰ — thứ tự là nội dung. */
  steps: (string | BuocHoiThoai)[];
  /** Điều cấm và ràng buộc — đúng ở MỌI bước. */
  rules: string[];
}

export interface Widget {
  title: string;
  greeting: string;
  primaryColor: string;
  position: WidgetPosition;
  avatarUrl: string | null;
  /** Khung nhúng bật/tắt — TÁCH khỏi `active` của bot. */
  enabled: boolean;
  /** Định danh CÔNG KHAI của khung (nằm trong `<script>` trang khách). KHÔNG phải bí mật. */
  embedKey: string;
  /** Gốc được phép nhúng. RỖNG = KHÔNG gốc nào, không phải "mọi gốc". */
  allowedOrigins: string[];
  rateLimitPerMinute: number;
  rateLimitPerIpPerMinute: number;
  /** Vai mà lượt hỏi từ khung nhúng chạy dưới đó. RỖNG = KHÔNG đọc được số liệu. */
  vai: string;
  /** Provider mô hình cho lượt nhúng. RỖNG = mặc định. Ai trả tiền thì người ấy chọn. */
  model: string;
}

export interface Chatbot {
  id: string;
  name: string;
  active: boolean;
  /** Bot có trả lời được câu nào không — nền tảng TÍNH. Đọc trường này, KHÔNG tự suy. */
  answerable: boolean;
  shortDescription: string;
  purpose: string;
  mode: ChatbotMode;
  systemPrompt: string;
  knowledgeBaseIds: string[];
  /** Nguồn số liệu đã gắn — tối đa `TRAN_NGUON`. Mỗi nguồn một câu SQL riêng, không JOIN chéo. */
  sourceIds: string[];
  /** `sourceIds` kèm TÊN, CHỈ ĐỌC. Nguồn đã xoá còn mã trong `sourceIds` nhưng không có ở đây. */
  sources: NguonGon[];
  cheDoGiaoTiep: CheDoGiaoTiep;
  /** Mã giọng đọc, ví dụ `gemini:Zephyr`. Nằm ở BOT vì người nghe khung nhúng là khách vãng lai. */
  giong: string;
  /** Tên miền của chính tổ chức, để tra web khi bản sao và kho tri thức không có gì. RỖNG = không tra. */
  webDomains: string[];
  faqs: Faq[];
  scenariosEnabled: boolean;
  scenarios: Scenario[];
  saveHistory: boolean;
  /** Câu trả lời có nêu tên tệp/trang đã dẫn. */
  trichNguon: boolean;
  phamVi: PhamViBot;
  widget: Widget;
  createdAt: string;
  updatedAt: string;
}

/** Bot người dùng chat được — `GET /chatbots/dung-duoc`. */
export type ChatbotDungDuoc = Chatbot;

/**
 * Phần GỬI LÊN. `answerable` và `sources` là thứ nền tảng TÍNH, máy chủ khai `extra="forbid"` — gửi lên là 422.
 */
export type ChatbotInput = Omit<Chatbot, 'id' | 'createdAt' | 'updatedAt' | 'answerable' | 'sources'>;

export interface ChatMessage {
  id: string;
  turn: number;
  author: 'user' | 'bot';
  text: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export interface Conversation {
  id: string;
  chatbotId: string;
  /** Vai đã tạo đoạn chat. Đoạn chat khoá theo VAI (vai khác đọc ra 404) — giao diện phải nói được điều đó. */
  role: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  /** LUÔN rỗng ở đường liệt kê — mở một đoạn thì gọi `moDoan()`. */
  messages: ChatMessage[];
}

export const WIDGET_MAC_DINH: Widget = {
  title: '',
  greeting: '',
  primaryColor: WIDGET_MAU_MAC_DINH,
  position: 'bottom-right',
  avatarUrl: null,
  enabled: false,
  embedKey: '',
  allowedOrigins: [],
  rateLimitPerMinute: 20,
  rateLimitPerIpPerMinute: 6,
  vai: '',
  model: '',
};

export const BOT_MAC_DINH: ChatbotInput = {
  name: '',
  active: false,
  shortDescription: '',
  purpose: '',
  mode: 'knowledge_only',
  systemPrompt: '',
  knowledgeBaseIds: [],
  sourceIds: [],
  cheDoGiaoTiep: 'van_ban',
  giong: '',
  webDomains: [],
  faqs: [],
  scenariosEnabled: false,
  scenarios: [],
  saveHistory: true,
  trichNguon: false,
  phamVi: 'phong_ban_con',
  widget: WIDGET_MAC_DINH,
};

/**
 * Đúng những khoá được phép gửi lên, LẤY THEO DANH SÁCH chứ không trừ dần — trừ dần thì trường chỉ-đọc mới
 * (`answerable`) lọt vào thân `PATCH` và bị `extra="forbid"` từ chối.
 */
export function chiPhanGuiLen(b: Chatbot): ChatbotInput {
  const khoa = Object.keys(BOT_MAC_DINH) as (keyof ChatbotInput)[];
  return Object.fromEntries(khoa.map((k) => [k, b[k]])) as ChatbotInput;
}

/** Đọc một trường bắt buộc về dạng object, chịu cả bản khai cũ dạng chuỗi. */
export function docTruong(t: string | TruongBatBuoc): TruongBatBuoc {
  return typeof t === 'string' ? { ten: t, kieu: 'van_ban' } : t;
}

export function docBuoc(b: string | BuocHoiThoai): BuocHoiThoai {
  return typeof b === 'string' ? { ten: b, thuThap: false, kieu: 'van_ban' } : b;
}

/**
 * Đúng những khoá được phép GỬI LÊN của một dòng phân quyền. Bản xem (`GET`) và thân (`PUT`) đang trùng nhau là
 * TRÙNG HỢP, không phải hợp đồng — chép nguyên thứ đọc về rồi gửi lại là hình dạng đã hỏng một lần ở native.
 */
export function chiPhanGuiLenCuaQuyen(g: PermGrant): PermGrant {
  return {
    role: g.role,
    level: g.level,
    tables: g.tables.map((t) => ({ table: t.table, deniedColumns: t.deniedColumns })),
  };
}
